import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { i18n as I18nInstance } from "i18next";

const SUPPORTED_LANGUAGES = ["en", "es", "de"] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

const normalizeLanguage = (language?: string): SupportedLanguage => {
    if (!language) {
        return "en";
    }

    const base = language.toLowerCase().split("-")[0] as SupportedLanguage;

    if (SUPPORTED_LANGUAGES.includes(base)) {
        return base;
    }

    return "en";
};

const LANGUAGE_CLASS_PREFIX = "lang-";

const updateHtmlElement = (language: SupportedLanguage) => {
    if (typeof document === "undefined") {
        return;
    }

    const htmlElement = document.documentElement;

    if (!htmlElement) {
        return;
    }

    htmlElement.lang = language;

    const targetClass = `${LANGUAGE_CLASS_PREFIX}${language}`;

    Array.from(htmlElement.classList)
        .filter(
            (className) =>
                className.startsWith(LANGUAGE_CLASS_PREFIX) &&
                className !== targetClass
        )
        .forEach((className) => htmlElement.classList.remove(className));

    htmlElement.classList.add(targetClass);
};

const getFallbackLanguage = (
    rawFallback: I18nInstance["options"]["fallbackLng"]
) => {
    let fallback: string | undefined;

    if (typeof rawFallback === "string") {
        fallback = rawFallback;
    } else if (Array.isArray(rawFallback) && rawFallback.length > 0) {
        fallback = rawFallback[0];
    } else if (rawFallback && typeof rawFallback === "object") {
        const values = Object.values(rawFallback);
        const firstArray = values.find(
            (value): value is string[] =>
                Array.isArray(value) &&
                value.length > 0 &&
                typeof value[0] === "string"
        );

        fallback = firstArray ? firstArray[0] : undefined;
    }

    return fallback || "en";
};

const useDocumentLanguage = () => {
    const { i18n } = useTranslation();

    useEffect(() => {
        const fallback = getFallbackLanguage(i18n.options.fallbackLng);
        const primaryLanguage =
            i18n.language ||
            (Array.isArray(i18n.languages) ? i18n.languages[0] : undefined);
        const activeLanguage = normalizeLanguage(primaryLanguage || fallback);

        updateHtmlElement(activeLanguage);
    }, [i18n.language, i18n.languages, i18n.options.fallbackLng]);
};

export default useDocumentLanguage;
